import type { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { NavBack } from '@/components/nav-back.tsx';
import { PointEmpty, PointItem } from '@/components/point-item.tsx';
import { useScroller } from '@/hooks/use-scroller.ts';
import { MSG } from '@/lib/constants.ts';
import { fetchPointHistory } from '@/lib/user.api.ts';
import { returnUrl } from '@/lib/utils.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { DaoPoint } from '@/types/dao.ts';
import type { PageProps } from '@/types/props.ts';

/**
 * 포인트 전체 내역 (시안 history.html).
 * 바닥에 닿으면 다음 장을 이어 붙인다.
 */
const MypageHistory: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const hydrated = useAppSelector((state) => state.auth.hydrated);

  const scrollRef = useRef<HTMLDivElement>(null);

  const [points, setPoints] = useState<DaoPoint[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const userSeq = user?.userSeq;

  useEffect(() => {
    if (!hydrated) return;
    if (!user?.id) route(`/login?r=${encodeURIComponent(returnUrl())}`, true);
  }, [hydrated, user?.id]);

  useEffect(() => {
    if (!userSeq) return;

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const response = await fetchPointHistory(userSeq, page);
        if (cancelled) return;

        if (!response.result) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          return;
        }

        const items = response.data?.items ?? [];
        setTotalPages(response.data?.total_pages ?? 1);

        // 첫 장은 갈아끼우고, 이후는 이어 붙인다.
        setPoints((prev) => (page === 1 ? items : [...prev, ...items]));
      } catch {
        if (!cancelled) await dispatch(askAlert(MSG.apiFail));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userSeq, page, dispatch]);

  const handleScroll = useCallback(
    ({ atBottom }: { atBottom: boolean }) => {
      if (!atBottom || loading) return;
      setPage((prev) => (prev < totalPages ? prev + 1 : prev));
    },
    [loading, totalPages]
  );

  useScroller(scrollRef, handleScroll);

  return (
    <div class="page">
      <header class="title">
        <NavBack onBack={() => route('/mypage')} />
        <span class="nav-title">포인트 내역</span>
      </header>

      <div class="section" ref={scrollRef}>
        <div class="history-section history-view">
          <div class="history-list">
            {loaded && points.length < 1 && <PointEmpty />}
            {points.map((dao) => (
              <PointItem key={dao.pointSeq} dao={dao} />
            ))}
          </div>
          <LoadingSpinner show={loading} />
        </div>
      </div>
    </div>
  );
};

export default MypageHistory;
